/**
 * MODULE: _shared/frontend/lib/frontend/utils/brainPushConnection.ts
 * PURPOSE: Track brain.activity.changed SSE connection for adaptive poll backoff [2.2.1258].
 * DEPENDENCIES: react (hook only)
 * PUBLIC_API: BRAZN_BRAIN_PUSH_STATUS, isBrainPushConnected, notifyBrainPushStatus, useBrainPushConnected
 * SIDE_EFFECTS: window CustomEvent dispatch
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 48 / 100 lines
 */

import { useEffect, useState } from 'react';

export const BRAZN_BRAIN_PUSH_STATUS = 'brazn-brain-push-status';

export type BrainPushStatusDetail = {
  connected: boolean;
};

let pushConnected = false;

export function isBrainPushConnected(): boolean {
  return pushConnected;
}

export function notifyBrainPushStatus(detail: BrainPushStatusDetail): void {
  if (typeof window === 'undefined') return;
  pushConnected = detail.connected;
  window.dispatchEvent(new CustomEvent(BRAZN_BRAIN_PUSH_STATUS, { detail }));
}

/** Subscribe to SSE push connection state (used to slow safety-net polls when push is live). */
export function useBrainPushConnected(): boolean {
  const [connected, setConnected] = useState(() => isBrainPushConnected());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onStatus = (event: Event) => {
      const detail = (event as CustomEvent<BrainPushStatusDetail>).detail;
      setConnected(Boolean(detail?.connected));
    };

    window.addEventListener(BRAZN_BRAIN_PUSH_STATUS, onStatus);
    return () => window.removeEventListener(BRAZN_BRAIN_PUSH_STATUS, onStatus);
  }, []);

  return connected;
}
